import { observer } from 'mobx-react-lite'
import { useParams } from 'react-router-dom'
import DocViewPlanPageStore, { DocViewPlanPageStoreProvider } from './DocViewPlanPageStore'
import useStore from 'reusable/mobx-store-utils'
import useInitialMount from 'reusable/hooks/useInitialMount'
import { notNull } from '@gap/utils/utils'
import { LoadingModal } from 'reusable/mui/modals/LoadingModal'
import DocViewPlan from './DocViewPlan'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import { useOrgGuardStore } from 'features/main/main-router/org-guard/OrgGuardStore'
import DocViewBreadcrumb from '../reusable/DocViewBreadcrumb'
import { themeConfig } from '@gap/shared/theme'
import AccessDenied from 'features/access-denied/AccessDenied'
import OnlyForOrgType from 'reusable/containers/OnlyForOrgType'
import DownloadPdfButton from 'reusable/printing/DownloadPdfButton'
import ShareDocument from 'reusable/share-document-dialog/ShareDocument'
import { namingConfig } from '@gap/utils/config/naming-config'
import FlexAlignRight from 'reusable/mui/utilities/FlexAlignRight'

export interface DocViewPlanPageProps {
   finalised: boolean
}

export default observer(function DocViewPlanPage(props: DocViewPlanPageProps) {
   const orgGuardStore = useOrgGuardStore()
   const urlParams = useParams()
   // if the clientId is passed via the url i.e. /documents/client/:clientId, then use this,
   // otherwise use the orgId from the orgGuardStore (if accessed from a client documents page)
   const clientOrgId = urlParams.clientOrgId || notNull(orgGuardStore.orgId)

   const docViewPlanPageStore = useStore((sp) => new DocViewPlanPageStore(sp), { clientOrgId, orgGuardStore })
   useInitialMount(docViewPlanPageStore.fetch)

   if (!orgGuardStore.userHasBusinessPlanPermission) {
      return <AccessDenied ParentWrapper={null} type="content-access-denied" sx={{ mt: 6 }} />
   }

   if (docViewPlanPageStore.loader.isLoading) {
      return <LoadingModal visible title={`Loading ${namingConfig.businessPlan.singular}...`} />
   }

   return <DocViewPlanPageStoreProvider store={docViewPlanPageStore}>
      <OnlyForOrgType orgType="advisor">
         <DocViewBreadcrumb clientOrg={docViewPlanPageStore.clientOrg} docName={namingConfig.businessPlan.singular} sx={{ mt: 4 }} />
      </OnlyForOrgType>

      <Stack sx={{ mb: 4, alignItems: 'center' }}>
         <Box sx={{ width: themeConfig.breakpoints.values.md }}>
            <FlexAlignRight>
               <OnlyForOrgType orgType="advisor">
                  <ShareDocument document={docViewPlanPageStore.businessPlanShareDocument} clientOrgId={docViewPlanPageStore.clientOrg.id} />
               </OnlyForOrgType>

               <OnlyForOrgType orgType="client">
                  <DownloadPdfButton docTitle={docViewPlanPageStore.docTitle} documentUrl={docViewPlanPageStore.businessPlanDocumentUrl} />
               </OnlyForOrgType>
            </FlexAlignRight>
            <Paper elevation={12} sx={{ mt: 6, p: 6, pt: 5 }}>
               <DocViewPlan client={docViewPlanPageStore.clientOrg} finalised={props.finalised} />
            </Paper>
         </Box>
      </Stack>
   </DocViewPlanPageStoreProvider>
})
